import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Download, HelpCircle } from "lucide-react";
import { useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { formatDate } from "@/shared/lib/date-time";
import { usePayrollListQuery } from "../api/queries";
import { HrTour } from "../tour/HrTour";
import { payrollDetailTourSteps } from "../tour/hrTourSteps";
import { useHrTour } from "../tour/useHrTour";
import { PayslipPdfPrintShell } from "../ui/PayslipPdfPreviewContent";

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("lo-LA", {
    style: "decimal",
    minimumFractionDigits: 0,
  }).format(n);
}

export function PayrollHistoryPreviewPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/app/hr/payroll/preview" }) as {
    year?: string;
  };
  const year = search.year ? Number(search.year) : undefined;
  const tour = useHrTour("hr-payroll-preview-tour-completed");

  const query = usePayrollListQuery({
    year,
    limit: 500,
    offset: 0,
    enabled: year != null,
  });

  const rows = query.data?.data ?? [];
  const employeeName = rows[0]?.employeeName ?? "-";

  const paidRows = useMemo(
    () => rows.filter((r) => r.status === "paid"),
    [rows],
  );

  const totalNetPaidYtd = useMemo(() => {
    return paidRows.reduce((acc, r) => acc + r.netSalary, 0);
  }, [paidRows]);

  const paidPeriod = useMemo(() => {
    if (paidRows.length === 0) return "—";
    const MONTH_ABBR_EN = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    const months = paidRows.map((r) => r.periodMonth);
    const startMonth = Math.min(...months);
    const endMonth = Math.max(...months);
    return `${MONTH_ABBR_EN[startMonth - 1] ?? ""}-${MONTH_ABBR_EN[endMonth - 1] ?? ""}`;
  }, [paidRows]);

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <Header className="print:hidden" />
      <Main>
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between print:hidden">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-bold text-2xl tracking-tight">
                ການສຳເນົາປະຫວັດເງິນເດືອນ
              </h1>

              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-8"
                onClick={tour.startTour}
                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                title="ເລີ່ມທົດສອບການນຳທາງ"
              >
                <HelpCircle className="size-4" />
              </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ປີງົບປະມານ {year ?? "—"} — ໄລຍະເວລາ {paidPeriod}
            </p>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={handlePrint}>
              <Download className="mr-1 size-4" />
              ດາວໂຫລດ / ພິມ
            </Button>
            <Button
              size="sm"
              variant="default"
              onClick={() => navigate({ to: "/app/hr/payroll" })}
            >
              <ArrowLeft className="mr-1 size-4" />
              ກັບຄືນ
            </Button>
          </div>
        </div>

        <div data-tourid="hr-payroll-detail-content">
          <PayslipPdfPrintShell
            title="ປະຫວັດໃບຮັບເງິນເດືອນ"
            extraFooterLine={year != null ? `ປີງົບປະມານ ${year}` : undefined}
          >
            {/* Same top header style as single payslip (employee name block) */}
            <div className="mb-8 flex items-start justify-between border-gray-200 border-b pb-6">
              <div className="text-sm">
                <p className="font-semibold">
                  ພະນັກງານ: <span className="font-normal">{employeeName}</span>
                </p>
                <p className="mt-1 font-semibold">
                  ປີງົບປະມານ: <span className="font-normal">{year ?? "—"}</span>
                </p>
                <p className="mt-1 font-semibold">
                  ໄລຍະເວລາ: <span className="font-normal">{paidPeriod}</span>
                </p>
              </div>
              <div className="text-right text-sm">
                <p className="font-semibold">ວັນທີພິມ:</p>
                <p className="font-normal">
                  {formatDate(new Date().toISOString())}
                </p>
              </div>
            </div>

            <div className="">
              <p className="text-muted-foreground text-sm">ສະຫຼຸບປີ</p>
              <p className="mt-4 font-bold text-3xl">
                ₭ {formatCurrency(totalNetPaidYtd)}
              </p>
              <p className="mt-1 text-muted-foreground text-sm">
                ຍອດສຸດທິລວມຈ່າຍແລ້ວ (ປີ)
              </p>

              <div className="mt-6">
                <div className="mb-2 font-semibold text-sm">ໃບຮັບເງິນເດືອນ</div>
                <div className="w-full overflow-x-auto print:overflow-visible">
                  <table className="mb-6 w-full border-collapse border border-gray-300 text-xs sm:text-sm">
                    <colgroup>
                      <col className="w-1/3" />
                      <col className="w-1/3" />
                      <col className="w-1/3" />
                    </colgroup>
                    <thead>
                      <tr className="bg-gray-50">
                        <th
                          scope="col"
                          className="border border-gray-300 p-3 text-left font-semibold text-gray-700"
                        >
                          ເດືອນ
                        </th>
                        <th
                          scope="col"
                          className="border border-gray-300 p-3 text-left font-semibold text-gray-700"
                        >
                          ວັນທີຈ່າຍ
                        </th>
                        <th
                          scope="col"
                          className="border border-gray-300 p-3 text-right font-semibold text-gray-700"
                        >
                          ຍອດສຸດທິ
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.length === 0 ? (
                        <tr>
                          <td
                            className="border border-gray-300 p-3 text-center text-gray-900"
                            colSpan={3}
                          >
                            ບໍ່ມີໃບຮັບເງິນເດືອນສຳລັບປີນີ້
                          </td>
                        </tr>
                      ) : (
                        rows.map((r) => (
                          <tr key={r.id} className="border border-gray-300">
                            <td className="break-words border border-gray-300 p-3 text-gray-900 text-sm">
                              {r.periodLabel}
                            </td>
                            <td className="whitespace-nowrap border border-gray-300 p-3 text-gray-900 text-sm">
                              {r.paidAt ? formatDate(r.paidAt) : "—"}
                            </td>
                            <td className="whitespace-nowrap border border-gray-300 p-3 text-right text-gray-900 text-sm tabular-nums">
                              ₭ {formatCurrency(r.netSalary)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </PayslipPdfPrintShell>
        </div>

        <HrTour
          steps={payrollDetailTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
